import React, { useEffect, useRef, useState, useCallback } from "react";
import { OverlayView, InfoWindow } from "@react-google-maps/api";
import { getData } from "../../../Services/Services";
import { getIncidentPastWeatherData } from "../../../Services/EndPoints";
// ─── Facility Configuration ────────────────────────────────────────────────────
export const FACILITY_TYPES = [
  { key: "hospital",                label: "Hospital",         icon: "🏥", color: "#1D4E75", bg: "#EBF2F7", priority: 1,  type: "hospital",                radius: 15000 },
  { key: "fire_station",            label: "Fire Station",     icon: "🚒", color: "#245A85", bg: "#EDF4F9", priority: 2,  type: "fire_station",            radius: 20000 },
  { key: "police",                  label: "Police Station",   icon: "🚔", color: "#2A6591", bg: "#F0F6FB", priority: 3,  type: "police",                  radius: 20000 },
  { key: "gas_station",             label: "Fuel Station",     icon: "⛽", color: "#3072A3", bg: "#F2F8FD", priority: 4,  type: "gas_station",             radius: 15000 },
  { key: "pharmacy",                label: "Pharmacy",         icon: "💊", color: "#3680B5", bg: "#F5F9FE", priority: 5,  type: "pharmacy",                radius: 10000 },
  { key: "bank",                    label: "Bank / ATM",       icon: "🏦", color: "#3C8FC7", bg: "#F7FBFF", priority: 6,  type: "bank",                    radius: 10000 },
  { key: "school",                  label: "School (Shelter)", icon: "🏫", color: "#2A6591", bg: "#F0F6FB", priority: 7,  type: "school",                  radius: 15000 },
  { key: "lodging",                 label: "Hotel (Refuge)",   icon: "🏨", color: "#1D4E75", bg: "#EBF2F7", priority: 8,  type: "lodging",                 radius: 20000 },
  { key: "supermarket",             label: "Grocery / Supply", icon: "🛒", color: "#3072A3", bg: "#F2F8FD", priority: 9,  type: "supermarket",             radius: 10000 },
  { key: "local_government_office", label: "Govt. Office",     icon: "🏛️", color: "#245A85", bg: "#EDF4F9", priority: 10, type: "local_government_office", radius: 25000 },
];

// ─── Haversine Distance ────────────────────────────────────────────────────────
export const haversineDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371, dLat = ((lat2 - lat1) * Math.PI) / 180, dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return (R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(1);
};

// ─── Single Facility Marker ────────────────────────────────────────────────────
const FacilityMarker = ({ place, facilityConfig, openId, onOpen, onClose, mapInstance, nearbySearchCenter, isSelected }) => {
  const isOpen = openId === place.place_id;
  const SELECTED_BLUE = "#3B82F6";

  const handleClick = (e) => {
    e.stopPropagation();
    onOpen(place.place_id);
    if (mapInstance && place?.geometry?.location) {
      const lat = typeof place.geometry.location.lat === "function"
        ? place.geometry.location.lat()
        : place.geometry.location.lat;
      const lng = typeof place.geometry.location.lng === "function"
        ? place.geometry.location.lng()
        : place.geometry.location.lng;
      mapInstance.panTo({ lat, lng });
    }
  };

  return (
    <>
      <OverlayView position={place.geometry.location} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
        <div onClick={handleClick} title={place.name}
          style={{ position: "relative", transform: "translate(-14px,-14px)", cursor: "pointer", width: 28, height: 28 }}>

          {/* Animated blue pulse rings when selected */}
          {isSelected && (
            <>
              <div style={{
                position: "absolute", borderRadius: "50%",
                width: 64, height: 64,
                top: "50%", left: "50%",
                border: `2.5px solid ${SELECTED_BLUE}`,
                animation: "facilityPulse 1.8s ease-out infinite",
                pointerEvents: "none",
              }} />
              <div style={{
                position: "absolute", borderRadius: "50%",
                width: 64, height: 64,
                top: "50%", left: "50%",
                border: `2.5px solid ${SELECTED_BLUE}`,
                animation: "facilityPulse 1.8s ease-out 0.6s infinite",
                pointerEvents: "none",
              }} />
              <div style={{
                position: "absolute", borderRadius: "50%",
                width: 64, height: 64,
                top: "50%", left: "50%",
                border: `2.5px solid ${SELECTED_BLUE}`,
                animation: "facilityPulse 1.8s ease-out 1.2s infinite",
                pointerEvents: "none",
              }} />
              {/* Solid glow ring underneath */}
              <div style={{
                position: "absolute", borderRadius: "50%",
                width: 38, height: 38,
                top: "50%", left: "50%",
                transform: "translate(-50%, -50%)",
                background: `${SELECTED_BLUE}22`,
                border: `2px solid ${SELECTED_BLUE}88`,
                pointerEvents: "none",
                zIndex: 1,
              }} />
            </>
          )}

          <div style={{
            width: 28, height: 28, borderRadius: "50%",
            background: isSelected ? "#EFF6FF" : facilityConfig.bg,
            border: isSelected ? `2.5px solid ${SELECTED_BLUE}` : `2.5px solid ${facilityConfig.color}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14,
            boxShadow: isSelected
              ? `0 0 0 3px ${SELECTED_BLUE}33, 0 4px 18px ${SELECTED_BLUE}99`
              : `0 2px 8px ${facilityConfig.color}55`,
            transition: "box-shadow 0.25s ease, border-color 0.25s ease",
            position: "relative", zIndex: 2,
          }}>
            {facilityConfig.icon}
          </div>

          {place._distanceKm && (
            <div style={{
              position: "absolute", top: -8, left: "50%", transform: "translateX(-50%)",
              background: isSelected ? SELECTED_BLUE : facilityConfig.color,
              color: "white", fontSize: 8, fontWeight: 700,
              padding: "1px 4px", borderRadius: 6, whiteSpace: "nowrap", pointerEvents: "none",
              boxShadow: isSelected ? `0 2px 8px ${SELECTED_BLUE}55` : "none",
              transition: "background 0.25s ease",
              zIndex: 3,
            }}>
              {place._distanceKm} km
            </div>
          )}

          <style>{`
            @keyframes facilityPulse {
              0%   { transform: translate(-50%, -50%) scale(0.4); opacity: 0.9; }
              100% { transform: translate(-50%, -50%) scale(1.9); opacity: 0; }
            }
          `}</style>
        </div>
      </OverlayView>

      {isOpen && (
        <InfoWindow position={place.geometry.location} onCloseClick={onClose}
          options={{ pixelOffset: new window.google.maps.Size(0, -20) }}>
          <div style={{ minWidth: 200, fontFamily: "Segoe UI, sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, paddingBottom: 6, borderBottom: `2px solid ${facilityConfig.color}` }}>
              <span style={{ fontSize: 18 }}>{facilityConfig.icon}</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{place.name}</div>
                <div style={{ fontSize: 10, color: facilityConfig.color, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{facilityConfig.label}</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#475569", lineHeight: 1.6 }}>
              <div>📍 {place.vicinity || "Address not available"}</div>
              {place._distanceKm && <div style={{ marginTop: 4 }}>📏 <strong style={{ color: facilityConfig.color }}>{place._distanceKm} km</strong> from incident</div>}
            </div>
          </div>
        </InfoWindow>
      )}
    </>
  );
};

// ─── Directions Route: Incident → Facility ────────────────────────────────────
export const DirectionsRoute = ({ mapInstance, origin, destinationPlaceId, facilityConfig, onRouteFetched }) => {
  const rendererRef = useRef(null);

  useEffect(() => {
    if (!mapInstance || !origin || !destinationPlaceId || !window.google) return;

    const directionsService  = new window.google.maps.DirectionsService();
    const directionsRenderer = new window.google.maps.DirectionsRenderer({
      suppressMarkers: true,
      preserveViewport: true,
      polylineOptions: {
        strokeColor:   "#1A73E8",   // Google Maps standard navigation blue — universally recognized
        strokeWeight:  6,
        strokeOpacity: 0.9,
        icons: [
          {
            icon: {
              path:         window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
              scale:        3,
              strokeColor:  "#ffffff",
              strokeWeight: 1,
              fillColor:    "#1A73E8",
              fillOpacity:  1,
            },
            offset: "100%",
            repeat: "120px",
          },
        ],
      },
    });

    directionsRenderer.setMap(mapInstance);
    rendererRef.current = directionsRenderer;

    const [lat, lng] = Array.isArray(origin) ? origin : [origin.lat, origin.lng];

    directionsService.route(
      {
        origin:      new window.google.maps.LatLng(lat, lng),
        destination: { placeId: destinationPlaceId },
        travelMode:  window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          directionsRenderer.setDirections(result);
          if (onRouteFetched) {
            const leg = result.routes[0]?.legs[0];
            onRouteFetched({
              distance: leg?.distance?.text || "",
              duration: leg?.duration?.text || "",
            });
          }
        } else {
          console.warn("Directions failed:", status);
          if (onRouteFetched) onRouteFetched(null);
        }
      }
    );

    return () => {
      directionsRenderer.setMap(null);
      rendererRef.current = null;
    };
  }, [mapInstance, origin, destinationPlaceId]);

  return null;
};

// ─── Hook: Nearby Facilities ──────────────────────────────────────────────────
export const useNearbyFacilities = (mapInstance, incidentCenter, enabled = true) => {
  const [facilities, setFacilities] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFacilities = useCallback(async () => {
    if (!mapInstance || !incidentCenter || !window.google || !enabled) return;
    setLoading(true); setError(null);
    const service = new window.google.maps.places.PlacesService(mapInstance);
    const [lat, lng] = Array.isArray(incidentCenter) ? incidentCenter : [incidentCenter.lat, incidentCenter.lng];
    const location = new window.google.maps.LatLng(lat, lng);
    const results = {};
    await Promise.all(FACILITY_TYPES.map((fac) => new Promise((resolve) => {
      service.nearbySearch({ location, radius: fac.radius, type: fac.type }, (places, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && places) {
          results[fac.key] = places
            .map((p) => ({ ...p, _distanceKm: haversineDistance(lat, lng, p.geometry.location.lat(), p.geometry.location.lng()) }))
            .sort((a, b) => parseFloat(a._distanceKm) - parseFloat(b._distanceKm))
            .slice(0, 5);
        } else { results[fac.key] = []; }
        resolve();
      });
    })));
    setFacilities(results); setLoading(false);
  }, [mapInstance, incidentCenter, enabled]);

  useEffect(() => {
    if (enabled && incidentCenter) fetchFacilities();
    else if (!enabled) setFacilities({});
  }, [fetchFacilities, enabled, incidentCenter]);

  return { facilities, loading, error, refetch: fetchFacilities };
};

// ═══════════════════════════════════════════════════════════════════════════════
// GOOGLE WEATHER API  —  weather.googleapis.com/v1
// ═══════════════════════════════════════════════════════════════════════════════
const GW_BASE = "https://weather.googleapis.com/v1";

const COND_EMOJI = {
  CLEAR: "☀️", MOSTLY_CLEAR: "🌤️", PARTLY_CLOUDY: "⛅", MOSTLY_CLOUDY: "🌥️", CLOUDY: "☁️",
  WINDY: "💨", WIND_AND_RAIN: "🌧️", LIGHT_RAIN: "🌦️", RAIN: "🌧️", RAIN_SHOWERS: "🌧️",
  HEAVY_RAIN: "🌧️", THUNDERSTORM: "⛈️", HEAVY_THUNDERSTORM: "⛈️", SCATTERED_THUNDERSTORMS: "⛈️",
  LIGHT_THUNDERSTORM_RAIN: "⛈️", THUNDERSHOWER: "⛈️", SCATTERED_SHOWERS: "🌦️",
  DRIZZLE: "🌦️", LIGHT_DRIZZLE: "🌦️", FREEZING_DRIZZLE: "🌨️",
  LIGHT_SNOW: "❄️", SNOW: "❄️", HEAVY_SNOW: "❄️", BLIZZARD: "❄️",
  SLEET: "🌨️", ICE: "🧊", HAIL: "🌨️", FOGGY: "🌫️", HAZY: "🌫️",
  SMOKY: "🌫️", DUST: "🌫️", TORNADO: "🌪️", TROPICAL_STORM: "🌀", HURRICANE: "🌀",
};
const condEmoji = (t) => COND_EMOJI[t] || "🌡️";

const getRisk = (type = "", windKph = 0, precipPct = 0, thunderPct = 0) => {
  const CRIT = ["TORNADO", "HURRICANE", "TROPICAL_STORM", "BLIZZARD", "HEAVY_THUNDERSTORM"];
  const HIGH = ["THUNDERSTORM", "SCATTERED_THUNDERSTORMS", "HEAVY_RAIN", "HEAVY_SNOW", "THUNDERSHOWER", "LIGHT_THUNDERSTORM_RAIN", "WIND_AND_RAIN"];
  const MOD  = ["RAIN", "RAIN_SHOWERS", "SNOW", "SLEET", "ICE", "HAIL", "WINDY", "FOGGY", "SMOKY", "DUST"];
  const lbl  = type.replace(/_/g, " ");
  if (CRIT.includes(type) || thunderPct > 60 || windKph > 80) return { level: "CRITICAL", color: "#DC2626", label: lbl };
  if (HIGH.includes(type) || windKph > 40  || precipPct > 70) return { level: "HIGH",     color: "#F97316", label: lbl };
  if (MOD.includes(type)  || windKph > 20  || precipPct > 40) return { level: "MODERATE", color: "#EAB308", label: lbl };
  return { level: "LOW", color: "#10B981", label: "Conditions Normal" };
};

const ALERT_COLOR = { EXTREME: "#DC2626", SEVERE: "#F97316", MODERATE: "#EAB308", MINOR: "#3B82F6", UNKNOWN_SEVERITY: "#6B7280" };
const ALERT_ICON  = { EXTREME: "🚨",      SEVERE: "⚠️",      MODERATE: "🔶",      MINOR: "ℹ️",      UNKNOWN_SEVERITY: "📢" };

// ─── Hook: Google Weather API (Current + Hourly + 10-Day + Alerts) ────────────
export const useGoogleWeather = (incidentCenter, apiKey, enabled = true) => {
  const [currentWeather, setCurrentWeather] = useState(null);
  const [hourlyForecast,  setHourlyForecast]  = useState([]);
  const [dailyForecast,   setDailyForecast]   = useState([]);
  const [weatherAlerts,   setWeatherAlerts]   = useState([]);
  const [weatherLoading,  setWeatherLoading]  = useState(false);
  const [weatherError,    setWeatherError]    = useState(null);

  const fetchWeather = useCallback(async () => {
    if (!incidentCenter || !apiKey || !enabled) return;
    const [lat, lng] = Array.isArray(incidentCenter) ? incidentCenter : [incidentCenter.lat, incidentCenter.lng];
    setWeatherLoading(true);
    setWeatherError(null);

    const baseParams = new URLSearchParams({
      key: apiKey,
      "location.latitude":  lat,
      "location.longitude": lng,
      languageCode: "en",
      unitsSystem: "METRIC",
    });

    try {
      const curRes = await fetch(`${GW_BASE}/currentConditions:lookup?${baseParams}`);
      if (!curRes.ok) {
        const errData = await curRes.json().catch(() => ({}));
        throw new Error(errData?.error?.message || `HTTP ${curRes.status} on currentConditions`);
      }
      setCurrentWeather(await curRes.json());

      const fcParams = new URLSearchParams(baseParams);
      fcParams.set("days", "10");
      const fcRes = await fetch(`${GW_BASE}/forecast/days:lookup?${fcParams}`);
      if (fcRes.ok) {
        const fd = await fcRes.json();
        setDailyForecast(fd.forecastDays || []);
      }

      const hrParams = new URLSearchParams(baseParams);
      hrParams.set("hours", "24");
      const hrRes = await fetch(`${GW_BASE}/forecast/hours:lookup?${hrParams}`);
      if (hrRes.ok) {
        const hd = await hrRes.json();
        setHourlyForecast(hd.forecastHours || []);
      }

      const alParams = new URLSearchParams({ key: apiKey, "location.latitude": lat, "location.longitude": lng, languageCode: "en" });
      const alRes = await fetch(`${GW_BASE}/publicAlerts:lookup?${alParams}`);
      if (alRes.ok) {
        const ad = await alRes.json();
        setWeatherAlerts(ad.publicAlerts || []);
      }
    } catch (err) {
      setWeatherError(err.message);
    } finally {
      setWeatherLoading(false);
    }
  }, [incidentCenter, apiKey, enabled]);

  useEffect(() => {
    if (enabled && incidentCenter && apiKey) fetchWeather();
    else if (!enabled) { setCurrentWeather(null); setHourlyForecast([]); setDailyForecast([]); setWeatherAlerts([]); }
  }, [fetchWeather, enabled, incidentCenter, apiKey]);

  return { currentWeather, hourlyForecast, dailyForecast, weatherAlerts, weatherLoading, weatherError, refetch: fetchWeather };
};

// ─── Shared styles ────────────────────────────────────────────────────────────
const btnBase = {
  display: "flex", alignItems: "center", gap: 5, padding: "6px 14px",
  borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: "pointer",
  border: "1.5px solid", transition: "all 0.18s ease", whiteSpace: "nowrap",
};

// ─── Weather Panel ────────────────────────────────────────────────────────────
export const WeatherPanel = ({ incidentCenter, incidentName, apiKey, enabled, theme = "light", displayDateTime, pastWeatherData, pastWeatherLoading, weatherIncidentDate }) => {
  const [activeView, setActiveView] = useState("current");

  const { currentWeather: cw, hourlyForecast, dailyForecast, weatherAlerts, weatherLoading, weatherError, refetch } =
    useGoogleWeather(incidentCenter, apiKey, enabled);

  const isDark = theme === "dark";
  const bg   = isDark ? "#1e2d45" : "#ffffff";
  const text = isDark ? "#e2e8f0" : "#0F2D42";
  const sub  = isDark ? "#94a3b8" : "#3A5F78";
  const bdr  = isDark ? "#2d3f5c" : "rgba(42,101,145,0.10)";
  const card = isDark ? "#162238" : "#ffffff";
  const blue = "#2A6591";

  if (!enabled || !incidentCenter) return null;

  const condType   = cw?.weatherCondition?.type || "";
  const condLabel  = cw?.weatherCondition?.description?.text || "—";
  const iconUri    = cw?.weatherCondition?.iconBaseUri ? `${cw.weatherCondition.iconBaseUri}.png` : null;
  const tempC      = cw?.temperature?.degrees           != null ? Math.round(cw.temperature.degrees)           : "—";
  const feelsC     = cw?.feelsLikeTemperature?.degrees  != null ? Math.round(cw.feelsLikeTemperature.degrees)  : "—";
  const humidity   = cw?.relativeHumidity  ?? "—";
  const windSpd    = cw?.wind?.speed?.value   != null ? `${cw.wind.speed.value} km/h`  : "—";
  const windGust   = cw?.wind?.gust?.value    != null ? `${cw.wind.gust.value} km/h`   : null;
  const windCard   = cw?.wind?.direction?.cardinal?.replace(/_/g, " ") || "";
  const uvIdx      = cw?.uvIndex ?? "—";
  const pressure   = cw?.airPressure?.meanSeaLevelMillibars != null ? `${Math.round(cw.airPressure.meanSeaLevelMillibars)} hPa` : "—";
  const visib      = cw?.visibility?.distance?.value != null ? `${(cw.visibility.distance.value / 1000).toFixed(1)} km` : "—";
  const precipPct  = cw?.precipitation?.probability?.percent ?? 0;
  const thunderPct = cw?.thunderstormProbability ?? 0;
  const isDaytime  = cw?.isDaytime ?? true;
  const tzId       = cw?.timeZone?.id || "";
  const localTime  = cw?.currentTime
    ? new Date(cw.currentTime).toLocaleTimeString("en-IN", { timeZone: tzId || undefined, hour: "2-digit", minute: "2-digit" })
    : "—";
  const dewPt      = cw?.dewPoint?.degrees != null ? `${Math.round(cw.dewPoint.degrees)}°C` : "—";
  const risk       = cw ? getRisk(condType, cw?.wind?.speed?.value || 0, precipPct, thunderPct) : null;

  const VIEW_BTNS = [
    { id: "historical", label: "Incident At", icon: "📜" },
    { id: "current", label: "Current",    icon: "🌡️" },
    { id: "hourly",  label: "24-Hour",    icon: "⏱️" },
    { id: "tenday",  label: "5-Day",     icon: "📅" },
  ];

  return (
    <div style={{ background: bg, color: text, borderTop: `1px solid ${bdr}`, fontFamily: "'Inter','Segoe UI',sans-serif" }}>

      <div style={{
        background: isDark ? "linear-gradient(135deg,#061525,#0d2040)" : "linear-gradient(135deg,#EBF4FA,#DCEdf5)",
        padding: "12px 14px", borderBottom: `1px solid ${bdr}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: text, display: "flex", alignItems: "center", gap: 6 }}>
            🌦️ <span style={{ fontFamily: "'Sora',sans-serif" }}>Weather Report</span>
            {weatherLoading && (
              <span style={{ fontSize: 10, color: blue, background: isDark ? "#0d2040" : "#e0f2fe", padding: "1px 8px", borderRadius: 8, fontWeight: 600, animation: "wpulse 1.4s ease infinite" }}>
                Fetching…
              </span>
            )}
            {weatherAlerts.length > 0 && (
              <span style={{ fontSize: 10, color: "#DC2626", background: "#FEF2F2", padding: "1px 8px", borderRadius: 8, fontWeight: 700, border: "1px solid #FECACA" }}>
                {weatherAlerts.length} Alert{weatherAlerts.length > 1 ? "s" : ""}
              </span>
            )}
          </div>
          {incidentName && <div style={{ fontSize: 10, color: sub, marginTop: 2 }}>At: {incidentName}</div>}
        </div>
        <button onClick={refetch} disabled={weatherLoading} title="Refresh weather"
          style={{ background: "transparent", border: `1px solid ${blue}55`, borderRadius: 6, padding: "3px 10px", cursor: "pointer", color: blue, fontSize: 11, fontWeight: 600, opacity: weatherLoading ? 0.5 : 1 }}>
          🔄 Refresh
        </button>
      </div>

      <div style={{
        display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, padding: "10px 14px",
        borderBottom: `1px solid ${bdr}`,
        background: isDark ? "#111e30" : "#F5FBFF",
      }}>
        {VIEW_BTNS.map((btn) => {
          const isActive = activeView === btn.id;
          return (
            <button
              key={btn.id}
              onClick={() => setActiveView(btn.id)}
              style={{
                ...btnBase,
                justifyContent: "center",
                background: isActive ? blue : "transparent",
                color: isActive ? "#fff" : sub,
                borderColor: isActive ? blue : bdr,
                boxShadow: isActive ? `0 2px 10px ${blue}55` : "none",
                transform: isActive ? "translateY(-1px)" : "none",
              }}
            >
              <span>{btn.icon}</span> {btn.label}
            </button>
          );
        })}
      </div>

      {weatherError && (
        <div style={{ padding: 14, background: "#FEF2F2", borderBottom: `1px solid ${bdr}`, textAlign: "center" }}>
          <div style={{ fontSize: 22, marginBottom: 4 }}>⚠️</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#DC2626" }}>Weather data unavailable</div>
          <div style={{ fontSize: 10, color: "#64748b", marginTop: 3, padding: "0 8px", lineHeight: 1.5 }}>
            {weatherError.includes("403")
              ? "API key does not have access to the Weather API. Enable it in Google Cloud Console."
              : weatherError.includes("404")
              ? "Weather API endpoint not found. Check that the Weather API is enabled in Google Cloud Console."
              : weatherError}
          </div>
          <button onClick={refetch}
            style={{ marginTop: 8, padding: "4px 14px", background: "#EF4444", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
            Retry
          </button>
        </div>
      )}

      {weatherLoading && !cw && (
        <div style={{ padding: 20, textAlign: "center", color: sub }}>
          <div style={{ fontSize: 32, marginBottom: 8, display: "inline-block", animation: "wspin 2s linear infinite" }}>🌀</div>
          <div style={{ fontSize: 13 }}>Loading live weather…</div>
        </div>
      )}

      {cw && !weatherError && activeView === "current" && (
        <div>
          {weatherAlerts.length > 0 && (
            <div style={{ borderBottom: `1px solid ${bdr}` }}>
              {weatherAlerts.slice(0, 3).map((alert, i) => {
                const sev = alert.severity || "UNKNOWN_SEVERITY";
                const color = ALERT_COLOR[sev] || "#6B7280";
                const icon  = ALERT_ICON[sev]  || "📢";
                return (
                  <div key={i} style={{
                    background: `${color}12`, borderLeft: `4px solid ${color}`, padding: "9px 14px",
                    borderBottom: i < weatherAlerts.length - 1 ? `1px solid ${color}22` : "none",
                    display: "flex", alignItems: "flex-start", gap: 8,
                  }}>
                    <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 800, color, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        {sev.replace(/_/g, " ")} ALERT
                      </div>
                      <div style={{ fontSize: 11, color: text, marginTop: 1, fontWeight: 600 }}>
                        {alert.headline?.text || alert.description?.text || "Active weather alert"}
                      </div>
                      {alert.event?.text && <div style={{ fontSize: 10, color: sub, marginTop: 1 }}>{alert.event.text}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {weatherAlerts.length === 0 && risk && risk.level !== "LOW" && (
            <div style={{
              background: `${risk.color}12`, borderLeft: `4px solid ${risk.color}`, padding: "8px 14px",
              display: "flex", alignItems: "center", gap: 8, borderBottom: `1px solid ${bdr}`
            }}>
              <span style={{ fontSize: 16 }}>{risk.level === "CRITICAL" ? "🚨" : risk.level === "HIGH" ? "⚠️" : "🔶"}</span>
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, color: risk.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>{risk.level} RISK</div>
                <div style={{ fontSize: 11, color: text }}>{risk.label.charAt(0).toUpperCase() + risk.label.slice(1).toLowerCase()}</div>
              </div>
            </div>
          )}

          <div style={{
            padding: "14px",
            background: isDark
              ? "linear-gradient(160deg,#0b1a2e,#122540)"
              : `linear-gradient(160deg,${isDaytime ? "#f0f9ff" : "#e8f0fe"},${isDaytime ? "#e0f2fe" : "#dbeafe"})`,
            borderBottom: `1px solid ${bdr}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 3 }}>
                <span style={{ fontSize: 48, lineHeight: 1, fontWeight: 800, color: text }}>{tempC}°</span>
                <span style={{ fontSize: 18, color: sub, marginBottom: 9, fontWeight: 600 }}>C</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: text, marginTop: 2, textTransform: "capitalize" }}>{condLabel}</div>
              <div style={{ fontSize: 11, color: sub, marginTop: 3 }}>Feels like {feelsC}°C &nbsp;·&nbsp; {isDaytime ? "☀️ Daytime" : "🌙 Night"}</div>
              <div style={{ fontSize: 10, color: sub, marginTop: 2 }}>🕐 {localTime}{tzId ? ` (${tzId})` : ""}</div>
            </div>
            <div style={{ textAlign: "center", flexShrink: 0 }}>
              {iconUri && (
                <img src={iconUri} alt={condLabel} style={{ width: 64, height: 64, objectFit: "contain" }}
                  onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "block"; }} />
              )}
              <span style={{ fontSize: 52, lineHeight: 1, display: iconUri ? "none" : "block" }}>{condEmoji(condType)}</span>
              <div style={{ marginTop: 4, fontSize: 10, color: risk?.color || blue, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {risk?.level || "—"}
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 1, background: bdr, borderBottom: `1px solid ${bdr}` }}>
            {[
              { icon: "💧", label: "Humidity",     value: `${humidity}%` },
              { icon: "🌬️", label: "Wind",         value: windSpd, sub: windGust ? `Gust ${windGust}` : windCard },
              { icon: "🧭", label: "Pressure",     value: pressure },
              { icon: "👁️", label: "Visibility",   value: visib },
              { icon: "🌂", label: "Rain Prob.",    value: `${precipPct}%` },
              { icon: "⚡", label: "Thunder Prob.", value: `${thunderPct}%` },
              { icon: "🔆", label: "UV Index",      value: uvIdx },
              { icon: "💨", label: "Wind Dir.",     value: windCard || "—" },
              { icon: "🌡️", label: "Dew Point",    value: dewPt },
            ].map((m) => (
              <div key={m.label} style={{ background: card, padding: "9px 6px", textAlign: "center" }}>
                <div style={{ fontSize: 16, marginBottom: 2 }}>{m.icon}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: text }}>{m.value}</div>
                {m.sub && <div style={{ fontSize: 9, color: blue, fontWeight: 600 }}>{m.sub}</div>}
                <div style={{ fontSize: 9, color: sub, marginTop: 1, textTransform: "uppercase", letterSpacing: "0.04em" }}>{m.label}</div>
              </div>
            ))}
          </div>
         
        </div>
      )}

      {!weatherError && activeView === "hourly" && (
        <div>
          {weatherLoading && !hourlyForecast.length ? (
            <div style={{ padding: 20, textAlign: "center", color: sub }}>
              <div style={{ fontSize: 28, marginBottom: 8, animation: "wspin 2s linear infinite", display: "inline-block" }}>🌀</div>
              <div style={{ fontSize: 13 }}>Loading hourly forecast…</div>
            </div>
          ) : hourlyForecast.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: sub }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>😔</div>
              <div style={{ fontSize: 13 }}>Hourly forecast not available</div>
            </div>
          ) : (
            <div>
              <div style={{ padding: "10px 14px 6px", fontSize: 11, fontWeight: 700, color: sub, textTransform: "uppercase", letterSpacing: "0.07em", borderBottom: `1px solid ${bdr}` }}>
                ⏱️ Next 24 Hours — Hour by Hour
              </div>
              <div>
                {hourlyForecast.map((hr, i) => {
                  const hrTime     = hr.interval?.startTime
                    ? new Date(hr.interval.startTime).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: tzId || undefined })
                    : `+${i}h`;
                  const hrDate     = hr.interval?.startTime
                    ? new Date(hr.interval.startTime).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", timeZone: tzId || undefined })
                    : "";
                  const hrTemp     = hr.temperature?.degrees         != null ? Math.round(hr.temperature.degrees)         : "—";
                  const hrFeels    = hr.feelsLikeTemperature?.degrees != null ? Math.round(hr.feelsLikeTemperature.degrees): null;
                  const hrHum      = hr.relativeHumidity ?? null;
                  const hrWind     = hr.wind?.speed?.value            != null ? `${hr.wind.speed.value} km/h`             : null;
                  const hrWindDir  = hr.wind?.direction?.cardinal?.replace(/_/g, " ") || null;
                  const hrPrecip   = hr.precipitation?.probability?.percent ?? null;
                  const hrThunder  = hr.thunderstormProbability       ?? null;
                  const hrCondType = hr.weatherCondition?.type        || "";
                  const hrCondLbl  = hr.weatherCondition?.description?.text || hrCondType.replace(/_/g, " ") || "—";
                  const hrIconUri  = hr.weatherCondition?.iconBaseUri ? `${hr.weatherCondition.iconBaseUri}.png` : null;
                  const hrVis      = hr.visibility?.distance?.value   != null ? `${(hr.visibility.distance.value / 1000).toFixed(1)} km` : null;
                  const hrUV       = hr.uvIndex ?? null;
                  const hrRisk     = getRisk(hrCondType, hr.wind?.speed?.value || 0, hrPrecip || 0, hrThunder || 0);
                  const isNight    = hr.isDaytime === false;
                  const isFirst    = i === 0;

                  return (
                    <div key={i} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 14px",
                      borderBottom: `1px solid ${bdr}`,
                      background: isFirst
                        ? isDark ? "#162e40" : "#f0f9ff"
                        : isNight
                        ? isDark ? "#121e30" : "#f5f3ff"
                        : bg,
                      borderLeft: `3px solid ${hrRisk.color}`,
                      transition: "background 0.15s",
                    }}>
                      <div style={{ flexShrink: 0, width: 58, textAlign: "center" }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: isFirst ? blue : text }}>{isFirst ? "Now" : hrTime}</div>
                        <div style={{ fontSize: 9, color: sub, marginTop: 1 }}>{hrDate}</div>
                        <div style={{ fontSize: 9, color: sub, marginTop: 1 }}>{isNight ? "🌙" : "☀️"}</div>
                      </div>
                      <div style={{ flexShrink: 0, width: 36, textAlign: "center" }}>
                        {hrIconUri
                          ? <img src={hrIconUri} alt={hrCondType} style={{ width: 34, height: 34, objectFit: "contain" }}
                              onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "inline"; }} />
                          : null}
                        <span style={{ fontSize: 28, display: hrIconUri ? "none" : "inline" }}>{condEmoji(hrCondType)}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: text, marginBottom: 2 }}>
                          {hrTemp}°C
                          {hrFeels !== null && <span style={{ fontSize: 10, color: sub, fontWeight: 500, marginLeft: 4 }}>Feels {hrFeels}°</span>}
                        </div>
                        <div style={{ fontSize: 10, color: sub, textTransform: "capitalize", marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {hrCondLbl}
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 8px" }}>
                          {hrHum    !== null && <span style={{ fontSize: 9, color: "#06B6D4" }}>💧{hrHum}%</span>}
                          {hrPrecip !== null && hrPrecip > 0 && <span style={{ fontSize: 9, color: blue }}>🌂{hrPrecip}%</span>}
                          {hrThunder !== null && hrThunder > 0 && <span style={{ fontSize: 9, color: "#F59E0B" }}>⚡{hrThunder}%</span>}
                          {hrWind   && <span style={{ fontSize: 9, color: sub }}>💨{hrWind}{hrWindDir ? ` ${hrWindDir}` : ""}</span>}
                          {hrVis    && <span style={{ fontSize: 9, color: sub }}>👁️{hrVis}</span>}
                          {hrUV     !== null && hrUV > 0 && <span style={{ fontSize: 9, color: "#F97316" }}>🔆UV{hrUV}</span>}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0, textAlign: "center" }}>
                        <div style={{
                          fontSize: 8, fontWeight: 800, color: hrRisk.color,
                          background: `${hrRisk.color}18`, border: `1px solid ${hrRisk.color}55`,
                          borderRadius: 8, padding: "2px 5px", textTransform: "uppercase", letterSpacing: "0.04em"
                        }}>
                          {hrRisk.level}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {!weatherError && activeView === "tenday" && (
        <div>
          {weatherLoading && !dailyForecast.length ? (
            <div style={{ padding: 20, textAlign: "center", color: sub }}>
              <div style={{ fontSize: 28, marginBottom: 8, animation: "wspin 2s linear infinite", display: "inline-block" }}>🌀</div>
              <div style={{ fontSize: 13 }}>Loading 10-day forecast…</div>
            </div>
          ) : dailyForecast.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: sub }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>😔</div>
              <div style={{ fontSize: 13 }}>10-day forecast not available</div>
            </div>
          ) : (
            <div>
              <div style={{ padding: "10px 14px 6px", fontSize: 11, fontWeight: 700, color: sub, textTransform: "uppercase", letterSpacing: "0.07em", borderBottom: `1px solid ${bdr}` }}>
                📅 10-Day Forecast
              </div>
              <div>
                {dailyForecast.map((day, i) => {
                  const dd     = day.displayDate;
                  const date   = dd ? new Date(dd.year, dd.month - 1, dd.day) : null;
                  const dayLbl = date ? date.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }) : `Day ${i + 1}`;
                  const maxT   = day.maxTemperature?.degrees != null ? Math.round(day.maxTemperature.degrees) : "—";
                  const minT   = day.minTemperature?.degrees != null ? Math.round(day.minTemperature.degrees) : "—";
                  const dayF   = day.daytimeForecast  || {};
                  const ngtF   = day.nighttimeForecast || {};
                  const fType  = dayF?.weatherCondition?.type || "";
                  const fLbl   = dayF?.weatherCondition?.description?.text || fType.replace(/_/g, " ") || "—";
                  const fIco   = dayF?.weatherCondition?.iconBaseUri ? `${dayF.weatherCondition.iconBaseUri}.png` : null;
                  const nType  = ngtF?.weatherCondition?.type || "";
                  const nLbl   = ngtF?.weatherCondition?.description?.text || nType.replace(/_/g, " ") || "—";
                  const nIco   = ngtF?.weatherCondition?.iconBaseUri ? `${ngtF.weatherCondition.iconBaseUri}.png` : null;
                  const fPcp   = dayF?.precipitation?.probability?.percent ?? null;
                  const fWnd   = dayF?.wind?.speed?.value ?? null;
                  const fThund = dayF?.thunderstormProbability ?? null;
                  const fHum   = dayF?.relativeHumidity ?? null;
                  const sunriseTime = day.sunEvents?.sunriseTime
                    ? new Date(day.sunEvents.sunriseTime).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: tzId || undefined })
                    : null;
                  const sunsetTime = day.sunEvents?.sunsetTime
                    ? new Date(day.sunEvents.sunsetTime).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: tzId || undefined })
                    : null;
                  const moonPhase = day.moonEvents?.moonPhase || null;
                  const dayRisk = getRisk(fType, fWnd || 0, fPcp || 0, fThund || 0);
                  const isToday = i === 0;

                  return (
                    <div key={i} style={{
                      padding: "12px 14px",
                      borderBottom: `1px solid ${bdr}`,
                      background: isToday ? (isDark ? "#162e40" : "#f0f9ff") : bg,
                      borderLeft: `3px solid ${dayRisk.color}`,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 800, color: isToday ? blue : text }}>
                            {isToday ? "Today" : dayLbl}
                          </div>
                          <div style={{ fontSize: 10, color: sub, marginTop: 1, textTransform: "capitalize" }}>{fLbl}</div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ textAlign: "center" }}>
                            <div style={{ fontSize: 8, color: "#F59E0B", fontWeight: 700, marginBottom: 2 }}>DAY</div>
                            {fIco
                              ? <img src={fIco} alt={fType} style={{ width: 36, height: 36, objectFit: "contain" }}
                                  onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "inline"; }} />
                              : null}
                            <span style={{ fontSize: 28, display: fIco ? "none" : "inline" }}>{condEmoji(fType)}</span>
                          </div>
                          <div style={{ textAlign: "center" }}>
                            <div style={{ fontSize: 8, color: "#6366F1", fontWeight: 700, marginBottom: 2 }}>NIGHT</div>
                            {nIco
                              ? <img src={nIco} alt={nType} style={{ width: 36, height: 36, objectFit: "contain" }}
                                  onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "inline"; }} />
                              : null}
                            <span style={{ fontSize: 28, display: nIco ? "none" : "inline" }}>{condEmoji(nType)}</span>
                          </div>
                          <div style={{ textAlign: "center", minWidth: 54 }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color: "#EF4444", lineHeight: 1 }}>{maxT}°</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: blue, marginTop: 2 }}>{minT}°</div>
                            <div style={{ fontSize: 8, color: sub, marginTop: 2 }}>H / L</div>
                          </div>
                        </div>
                      </div>

                      {nLbl && nLbl !== "—" && (
                        <div style={{ fontSize: 10, color: "#6366F1", marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
                          🌙 Night: <span style={{ textTransform: "capitalize" }}>{nLbl}</span>
                        </div>
                      )}

                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 8px", marginBottom: 6 }}>
                        {fPcp   !== null && <span style={{ fontSize: 10, color: blue,     fontWeight: 600 }}>🌂 Rain {fPcp}%</span>}
                        {fThund !== null && fThund > 0 && <span style={{ fontSize: 10, color: "#F59E0B", fontWeight: 600 }}>⚡ Thunder {fThund}%</span>}
                        {fWnd   !== null && <span style={{ fontSize: 10, color: sub,      fontWeight: 600 }}>💨 {fWnd} km/h</span>}
                        {fHum   !== null && <span style={{ fontSize: 10, color: "#06B6D4",fontWeight: 600 }}>💧 {fHum}%</span>}
                        {sunriseTime && <span style={{ fontSize: 10, color: "#F59E0B", fontWeight: 600 }}>🌅 {sunriseTime}</span>}
                        {sunsetTime  && <span style={{ fontSize: 10, color: "#6366F1",  fontWeight: 600 }}>🌇 {sunsetTime}</span>}
                        {moonPhase   && <span style={{ fontSize: 10, color: sub,        fontWeight: 600 }}>🌙 {moonPhase.replace(/_/g, " ")}</span>}
                      </div>

                      <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <span style={{
                          fontSize: 9, fontWeight: 800, color: dayRisk.color,
                          background: `${dayRisk.color}18`, border: `1px solid ${dayRisk.color}55`,
                          borderRadius: 8, padding: "2px 7px", textTransform: "uppercase", letterSpacing: "0.04em"
                        }}>
                          {dayRisk.level === "LOW" ? "✅ Normal" : `${dayRisk.level === "CRITICAL" ? "🚨" : dayRisk.level === "HIGH" ? "⚠️" : "🔶"} ${dayRisk.level}`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {!weatherError && activeView === "historical" && (
        <div>
          <div style={{
            padding: "10px 14px 6px", fontSize: 11, fontWeight: 700,
            color: sub, textTransform: "uppercase", letterSpacing: "0.07em",
            borderBottom: `1px solid ${bdr}`,
          }}>
            📜 Incident-Day Weather History
          </div>

          {pastWeatherLoading && (
            <div style={{ padding: 20, textAlign: "center", color: sub }}>
              <div style={{ fontSize: 28, marginBottom: 8, animation: "wspin 2s linear infinite", display: "inline-block" }}>🌀</div>
              <div style={{ fontSize: 13 }}>Loading historical data…</div>
            </div>
          )}

          {!pastWeatherLoading && !pastWeatherData && (
            <div style={{ padding: 24, textAlign: "center", color: sub }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
              <div style={{ fontSize: 13 }}>Select an incident to view historical weather</div>
            </div>
          )}

          {!pastWeatherLoading && pastWeatherData && pastWeatherData.status === "NOT_FOUND" && (
            <div style={{ padding: 24, textAlign: "center", color: sub }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: isDark ? "#f59e0b" : "#d97706" }}>
                {pastWeatherData.message}
              </div>
            </div>
          )}

          {!pastWeatherLoading && pastWeatherData && pastWeatherData.status !== "NOT_FOUND" && (
            <div id="historical-scroll-container" style={{ maxHeight: 420, overflowY: "auto" }}>
              {(() => {
                const records = Array.isArray(pastWeatherData) ? pastWeatherData : [pastWeatherData];

                // ── Find the record nearest to the incident creation time ──
                let nearestIdx = -1;
                if (weatherIncidentDate) {
                  const parseIncidentTime = (str) => {
                    if (!str) return null;
                    const d = new Date(str);
                    if (!isNaN(d)) return d.getTime();
                    const months = { JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5, JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11 };
                    const m = str.match(/(\d{2})-(\w{3})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})\s*(AM|PM)/i);
                    if (m) {
                      let h = parseInt(m[4]);
                      if (m[7].toUpperCase() === "PM" && h !== 12) h += 12;
                      if (m[7].toUpperCase() === "AM" && h === 12) h = 0;
                      return new Date(parseInt(m[3]), months[m[2].toUpperCase()], parseInt(m[1]), h, parseInt(m[5]), parseInt(m[6])).getTime();
                    }
                    return null;
                  };

                  const incidentTs = parseIncidentTime(weatherIncidentDate);
                  if (incidentTs) {
                    let minDiff = Infinity;
                    records.forEach((rec, idx) => {
                      let recTs = null;
                      if (rec.currentTime) recTs = new Date(rec.currentTime).getTime();
                      else if (rec.createdOn) {
                        const [y, mo, d, h = 0, mn = 0] = rec.createdOn;
                        recTs = new Date(y, mo - 1, d, h, mn).getTime();
                      }
                      if (recTs !== null) {
                        const diff = Math.abs(recTs - incidentTs);
                        if (diff < minDiff) { minDiff = diff; nearestIdx = idx; }
                      }
                    });
                  }
                }

                return records.map((record, i) => {
                  const isNearest = i === nearestIdx;
                  // ── Time / Date ──
                  const recTime = record.currentTime
                    ? new Date(record.currentTime).toLocaleTimeString("en-IN", {
                      hour: "2-digit", minute: "2-digit", hour12: true,
                      timeZone: record.timezone?.id || tzId || undefined,
                    })
                    : record.createdOn
                      ? (() => {
                        const [y, mo, d, h, m] = record.createdOn;
                        const dt = new Date(y, mo - 1, d, h || 0, m || 0);
                        return dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
                      })()
                      : `#${i + 1}`;

                  const recDate = record.currentTime
                    ? new Date(record.currentTime).toLocaleDateString("en-IN", {
                      weekday: "short", day: "numeric", month: "short",
                      timeZone: record.timezone?.id || tzId || undefined,
                    })
                    : record.dataDate
                      ? (() => {
                        const [y, mo, d] = record.dataDate;
                        return new Date(y, mo - 1, d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
                      })()
                      : "";

                  // ── Weather condition ──
                  const hrCondType = record.weatherCondition?.type || "";
                  const hrCondLbl = record.weatherCondition?.description?.text || hrCondType.replace(/_/g, " ") || "—";
                  const hrIconUri = record.weatherCondition?.iconBaseUri ? `${record.weatherCondition.iconBaseUri}.png` : null;

                  // ── Temperature ──
                  const hrTemp = record.temperature?.degrees != null ? Math.round(record.temperature.degrees) : (record.temp != null ? Math.round(record.temp) : "—");
                  const hrFeels = record.feelsLikeTemperature?.degrees != null ? Math.round(record.feelsLikeTemperature.degrees) : null;

                  // ── Metrics ──
                  const hrHum = record.relativeHumidity ?? (record.humidity != null ? record.humidity : null);
                  const hrWind = record.wind?.speed?.value != null ? `${record.wind.speed.value} km/h` : (record.wind_speed != null ? `${record.wind_speed} km/h` : null);
                  const hrWindDir = record.wind?.direction?.cardinal?.replace(/_/g, " ") || null;
                  const hrPrecip = record.precipitation?.probability?.percent ?? null;
                  const hrThunder = record.thunderstormProbability ?? null;
                  const hrVis = record.visibility?.distance != null
                    ? (typeof record.visibility.distance === "object"
                      ? `${(record.visibility.distance.value / 1000).toFixed(1)} km`
                      : `${record.visibility.distance} km`)
                    : null;
                  const hrUV = record.uvIndex ?? null;
                  const hrCloud = record.cloudCover ?? null;
                  const isNight = record.isDaytime === false;

                  // ── Risk badge ──
                  const hrRisk = getRisk(
                    hrCondType,
                    record.wind?.speed?.value || 0,
                    hrPrecip || 0,
                    hrThunder || 0
                  );

                  const isFirst = i === 0;

                  return (
                    <div
                      key={record.id || i}
                      ref={isNearest ? (el) => {
                        if (el) setTimeout(() => {
                          const container = document.getElementById("historical-scroll-container");
                          if (container) {
                            const elTop = el.offsetTop - container.offsetTop;
                            const elHeight = el.offsetHeight;
                            const containerHeight = container.clientHeight;
                            container.scrollTo({
                              top: elTop - (containerHeight / 2) + (elHeight / 2),
                              behavior: "smooth",
                            });
                          }
                        }, 300);
                      } : undefined}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "10px 14px",
                        borderBottom: `1px solid ${bdr}`,
                        background: isNearest
                          ? isDark ? "#1a3350" : "#FEF9C3"
                          : isFirst
                            ? isDark ? "#162e40" : "#f0f9ff"
                            : isNight
                              ? isDark ? "#121e30" : "#f5f3ff"
                              : bg,
                        borderLeft: isNearest
                          ? "4px solid #F59E0B"
                          : `3px solid ${hrRisk.color}`,
                        transition: "background 0.15s",
                        boxShadow: isNearest ? "0 0 12px rgba(245,158,11,0.3)" : "none",
                        position: "relative",
                      }}
                    >
                      {/* Nearest-match badge */}
                      {isNearest && (
                        <div style={{
                          position: "absolute", top: 4, right: 8,
                          background: "#F59E0B", color: "#fff",
                          fontSize: 8, fontWeight: 800, padding: "2px 7px",
                          borderRadius: 8, letterSpacing: "0.04em",
                          display: "flex", alignItems: "center", gap: 3,
                          animation: "selBadgePop 0.3s cubic-bezier(.175,.885,.32,1.275)",
                          zIndex: 5,
                        }}>
                          ⏱️ INCIDENT TIME
                        </div>
                      )}
                      {/* ── Left: Time block ── */}
                      <div style={{ flexShrink: 0, width: 58, textAlign: "center" }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: isFirst ? blue : text }}>{recTime}</div>
                        <div style={{ fontSize: 9, color: sub, marginTop: 1 }}>{recDate}</div>
                        <div style={{ fontSize: 9, color: sub, marginTop: 1 }}>{isNight ? "🌙" : "☀️"}</div>
                      </div>

                      {/* ── Weather icon ── */}
                      <div style={{ flexShrink: 0, width: 36, textAlign: "center" }}>
                        {hrIconUri
                          ? <img src={hrIconUri} alt={hrCondType} style={{ width: 34, height: 34, objectFit: "contain" }}
                            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "inline"; }} />
                          : null}
                        <span style={{ fontSize: 28, display: hrIconUri ? "none" : "inline" }}>{condEmoji(hrCondType)}</span>
                      </div>

                      {/* ── Center: temp + condition + metrics ── */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: text, marginBottom: 2 }}>
                          {hrTemp}°C
                          {hrFeels !== null && <span style={{ fontSize: 10, color: sub, fontWeight: 500, marginLeft: 4 }}>Feels {hrFeels}°</span>}
                        </div>
                        <div style={{ fontSize: 10, color: sub, textTransform: "capitalize", marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {hrCondLbl}
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 8px" }}>
                          {hrHum !== null && <span style={{ fontSize: 9, color: "#06B6D4" }}>💧{hrHum}%</span>}
                          {hrPrecip !== null && hrPrecip > 0 && <span style={{ fontSize: 9, color: blue }}>🌂{hrPrecip}%</span>}
                          {hrThunder !== null && hrThunder > 0 && <span style={{ fontSize: 9, color: "#F59E0B" }}>⚡{hrThunder}%</span>}
                          {hrWind && <span style={{ fontSize: 9, color: sub }}>💨{hrWind}{hrWindDir ? ` ${hrWindDir}` : ""}</span>}
                          {hrVis && <span style={{ fontSize: 9, color: sub }}>👁️{hrVis}</span>}
                          {hrUV !== null && hrUV > 0 && <span style={{ fontSize: 9, color: "#F97316" }}>🔆UV{hrUV}</span>}
                          {hrCloud !== null && <span style={{ fontSize: 9, color: "#8B5CF6" }}>☁️{hrCloud}%</span>}
                        </div>
                      </div>

                      {/* ── Right: Risk badge ── */}
                      <div style={{ flexShrink: 0, textAlign: "center" }}>
                        <div style={{
                          fontSize: 8, fontWeight: 800, color: hrRisk.color,
                          background: `${hrRisk.color}18`, border: `1px solid ${hrRisk.color}55`,
                          borderRadius: 8, padding: "2px 5px", textTransform: "uppercase", letterSpacing: "0.04em"
                        }}>
                          {hrRisk.level}
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes wpulse { 0%,100%{opacity:1} 50%{opacity:0.45} }
        @keyframes wspin  { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
};

// ─── Map Overlay: Facility Markers ────────────────────────────────────────────
export const NearbyFacilitiesMarkers = ({ facilities, enabledTypes, openPopupId, onOpen, onClose, mapInstance, nearbySearchCenter, selectedFacilityId }) => (
  <>
    {FACILITY_TYPES.filter((f) => enabledTypes.has(f.key)).map((facConfig) =>
      (facilities[facConfig.key] || []).map((place) => (
        <FacilityMarker
          key={place.place_id}
          place={place}
          facilityConfig={facConfig}
          openId={openPopupId}
          onOpen={onOpen}
          onClose={onClose}
          mapInstance={mapInstance}
          nearbySearchCenter={nearbySearchCenter}
          isSelected={selectedFacilityId === place.place_id}
        />
      ))
    )}
  </>
);

// ─── Right Panel: Nearby Facilities List ──────────────────────────────────────
export const NearbyFacilitiesPanel = ({ facilities, loading, enabledTypes, onToggleType, incidentName, theme = "light", mapInstance, nearbySearchCenter, selectedFacilityId, onSelectFacility }) => {
  const [expandedKey, setExpandedKey] = useState(null);
  const isDark = theme === "dark";
  const bg   = isDark ? "#1e2d45" : "#FFFFFF";
  const text = isDark ? "#e2e8f0" : "#0F2D42";
  const sub  = isDark ? "#94a3b8" : "#3A5F78";
  const bdr  = isDark ? "#2d3f5c" : "rgba(42,101,145,0.12)";
  const card = isDark ? "#1a2a40" : "#FFFFFF";
  const hov  = isDark ? "#243450" : "#F5FBFF";
  const hdr  = isDark ? "#162238" : "#FFFFFF";
  const totalFound = Object.values(facilities).reduce((s, p) => s + (p?.length || 0), 0);

  const BLUE = "#2A6591";

  const flyToFacility = (place) => {
    if (!mapInstance || !place?.geometry?.location) return;
    const lat = typeof place.geometry.location.lat === "function" ? place.geometry.location.lat() : place.geometry.location.lat;
    const lng = typeof place.geometry.location.lng === "function" ? place.geometry.location.lng() : place.geometry.location.lng;
    mapInstance.panTo({ lat, lng });
    mapInstance.setZoom(17);
  };

  const handleFacilityClick = (place) => {
    // Toggle selection: click again to deselect
    const newId = selectedFacilityId === place.place_id ? null : place.place_id;
    if (onSelectFacility) onSelectFacility(newId);
    flyToFacility(place);
  };

  return (
    <div style={{ background: bg, color: text, borderTop: `1px solid ${bdr}`, fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <div style={{ background: hdr, padding: "14px 16px", borderBottom: `1px solid ${bdr}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: text, display: "flex", alignItems: "center", gap: 6 }}>
            📍 <span style={{ fontFamily: "'Sora',sans-serif", letterSpacing: "-0.01em" }}>Nearby Essential Services</span>
            {loading && <span style={{ fontSize: 10, color: "#3B82F6", background: "#EFF6FF", padding: "1px 7px", borderRadius: 8, fontWeight: 600 }}>Searching…</span>}
            {!loading && totalFound > 0 && <span style={{ fontSize: 10, color: "#10B981", background: "#ECFDF5", padding: "1px 7px", borderRadius: 8, fontWeight: 600 }}>{totalFound} found</span>}
          </div>
          {incidentName && <div style={{ fontSize: 10, color: sub, marginTop: 2 }}>Around: {incidentName}</div>}
        </div>
      </div>

      {/* Type filter chips */}
      <div style={{ padding: "10px 14px", borderBottom: `1px solid ${bdr}`, background: "#FDFDFD", display: "flex", flexWrap: "wrap", gap: 6 }}>
        {FACILITY_TYPES.map((fac) => {
          const isOn = enabledTypes.has(fac.key), count = facilities[fac.key]?.length || 0;
          return (
            <button key={fac.key} onClick={() => onToggleType(fac.key)}
            title={fac.label}
              style={{
                display: "flex", alignItems: "center", gap: 4, padding: "4px 10px",
                border: `1px solid ${isOn ? fac.color : bdr}`, borderRadius: 20,
                background: isOn ? `${fac.color}15` : card, color: isOn ? fac.color : sub,
                fontSize: 11, fontWeight: 700, cursor: "pointer", 
                transition: "all 0.18s cubic-bezier(.16,1,.3,1)", 
                opacity: loading ? 0.6 : 1,
                boxShadow: isOn ? `0 2px 6px ${fac.color}22` : "0 1px 2px rgba(0,0,0,0.02)",
                fontFamily: "'Inter',sans-serif"
              }}>
              <span style={{ fontSize: 13 }}>{fac.icon}</span>
              {count > 0 && <span style={{ background: fac.color, color: "white", borderRadius: 10, padding: "1px 6px", fontSize: 9, fontWeight: 800, transform: "translateY(-0.5px)" }}>{count}</span>}
            </button>
          );
        })}
      </div>

      <div style={{ paddingBottom: 16 }}>
        {loading && <div style={{ padding: 20, textAlign: "center", color: sub, fontSize: 13 }}><div style={{ fontSize: 28, marginBottom: 8 }}>🔍</div>Searching nearby facilities…</div>}
        {!loading && FACILITY_TYPES.filter((f) => enabledTypes.has(f.key)).map((fac) => {
          const places = facilities[fac.key] || [];
          if (!places.length) return null;
          const isExp = expandedKey === fac.key;
          return (
            <div key={fac.key} style={{ borderBottom: `1px solid ${bdr}` }}>
              <div onClick={() => setExpandedKey(isExp ? null : fac.key)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "12px 14px", cursor: "pointer",
                  background: isExp ? "#F5FBFF" : card, 
                  transition: "all 0.18s ease",
                  borderLeft: isExp ? `3px solid ${fac.color}` : "3px solid transparent",
                }}
                onMouseEnter={e => { if (!isExp) e.currentTarget.style.background = "#FAFCFE"; }}
                onMouseLeave={e => { if (!isExp) e.currentTarget.style.background = card; }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", background: `${fac.color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: 13 }}>{fac.icon}</span>
                  </div>
                  <span style={{ fontSize: 12.5, fontWeight: 700, fontFamily: "'Sora',sans-serif", color: isExp ? text : text, letterSpacing: "-0.01em" }}>{fac.label}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ background: fac.color, color: "white", borderRadius: 12, padding: "1.5px 8px", fontSize: 10, fontWeight: 800 }}>{places.length}</span>
                  <span style={{ color: sub, fontSize: 12, width: 16, textAlign: "center", fontWeight: 700 }}>{isExp ? "—" : "＋"}</span>
                </div>
              </div>

              {isExp && (
                <div style={{ padding: "8px 12px 14px", background: "#F5FBFF", borderTop: `1px solid ${bdr}` }}>
                  {places.map((place, idx) => {
                    const isSel = selectedFacilityId === place.place_id;
                    return (
                      <div
                        key={place.place_id || idx}
                        onClick={() => handleFacilityClick(place)}
                        title={isSel ? "Click to deselect" : "Click to highlight on map"}
                        style={{
                          background: isSel ? "#FFFFFF" : card,
                          border: isSel ? `1.5px solid ${BLUE}` : `1px solid ${bdr}`,
                          borderRadius: 10, padding: "10px 12px", marginBottom: 8,
                          transition: "all 0.18s cubic-bezier(.16,1,.3,1)",
                          cursor: "pointer", position: "relative",
                          boxShadow: isSel ? `0 4px 16px ${BLUE}33` : "0 2px 6px rgba(15,45,66,0.03)",
                          transform: isSel ? "translateY(-1px)" : "none",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSel) {
                            e.currentTarget.style.background = hov;
                            e.currentTarget.style.transform = "translateX(2px)";
                            e.currentTarget.style.boxShadow = `0 2px 10px ${fac.color}33`;
                            e.currentTarget.style.borderColor = fac.color;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSel) {
                            e.currentTarget.style.background = card;
                            e.currentTarget.style.transform = "translateX(0)";
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.borderColor = bdr;
                          }
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 700, fontFamily: "'Sora',sans-serif", color: isSel ? BLUE : text, marginBottom: 4, display: "flex", alignItems: "flex-start", gap: 5, lineHeight: 1.3 }}>
                              {place.name}
                            </div>
                            <div style={{ fontSize: 10.5, color: sub, lineHeight: 1.45, fontWeight: 500 }}>{place.vicinity || "—"}</div>
                            {/* Status container removed */}
                          </div>
                          <div style={{
                            background: isSel ? `${BLUE}18` : `${fac.color}18`,
                            border: isSel ? `1.5px solid ${BLUE}55` : `1.5px solid ${fac.color}55`,
                            borderRadius: 8, padding: "4px 8px", textAlign: "center", minWidth: 50, flexShrink: 0,
                            transition: "background 0.2s ease, border-color 0.2s ease",
                          }}>
                            <div style={{ fontSize: 14, fontWeight: 800, color: isSel ? BLUE : fac.color, lineHeight: 1 }}>{place._distanceKm}</div>
                            <div style={{ fontSize: 9, color: isSel ? BLUE : fac.color, fontWeight: 600, opacity: 0.8 }}>km</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {!loading && totalFound === 0 && Object.keys(facilities).length > 0 && (
          <div style={{ padding: 20, textAlign: "center", color: sub, fontSize: 13 }}><div style={{ fontSize: 28, marginBottom: 8 }}>😔</div>No facilities found nearby</div>
        )}
      </div>

      <style>{`
        @keyframes selBadgePop {
          0%   { transform: scale(0.5); opacity: 0; }
          100% { transform: scale(1);   opacity: 1; }
        }
      `}</style>
    </div>
  );
};